import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { Label } from "../../../ui/label";
import { Checkbox } from "../../../ui/checkbox";
import { Button } from "../../../ui/button";
import { useAccessControlSettingsForm } from "./hooks";
import { InputField } from "../../../Shared/Radix/Inputs/InputField";
import { TextAreaField } from "../../../Shared/Radix/Inputs/TextAreaField";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../ui/card";

const BANNER_PATTERN = /^[^<>?;%/\\&[\]{}]*$/;
const BANNER_PATTERN_MSG =
  "Invalid input, <>[]{}&?;%/\\ characters not allowed";

const nestedPanelStyle = {
  backgroundColor: "color-mix(in srgb, var(--muted) 35%, var(--background))",
};

export const Settings: React.FC = () => {
  const [isEditMode, setIsEditMode] = useState(false);
  const {
    formData,
    setFormData,
    cancelHandler,
    submitHandler,
    isLoading,
    minimumAgeDays,
    expiryWarningDays,
    minimumLength,
    reusePreventionCount,
    reusePreventionDays,
    accountLockoutFailedCount,
    inactivityLogoutDurationMinutes,
    bannerTitle,
    messageOfDay,
    errorsPresent,
    handleDisabled,
    onMinAgeOrWarningDaysBlurHandler,
  } = useAccessControlSettingsForm();

  const onFieldChange = (name: string, value: string) => {
    setFormData((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleEnterEditMode = () => {
    setIsEditMode(true);
  };

  const handleCancelEditMode = () => {
    cancelHandler();
    setIsEditMode(false);
  };

  const handleSaveClick = async (event: React.FormEvent) => {
    const isSuccess = await submitHandler(event);
    if (isSuccess) {
      setIsEditMode(false);
    }
  };

  const renderValue = (value: string | number | boolean | null | undefined) => {
    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    if (value === undefined || value === null || value === "") {
      return "-";
    }

    return value;
  };

  const renderReadOnlyField = (
    label: string,
    value: string | number | boolean | null | undefined,
    required = false,
  ) => (
    <div className="flex flex-col gap-0.5">
      <span className="text-sm text-muted-foreground">
        {label}
        {required && <span className="text-destructive ml-0.5">*</span>}
      </span>
      <div className="text-sm font-semibold text-foreground">{renderValue(value)}</div>
    </div>
  );

  return (
    <div
      className="flex min-w-0 w-full flex-col overflow-x-hidden overflow-y-hidden px-4 pt-1 pb-6 md:px-3"
      style={{ color: "var(--foreground)" }}
    >
      <Card>
        <CardHeader className="flex flex-col gap-6 space-y-0 p-3 pl-6 pr-6 pt-4 pb-0">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-1.5">
              <CardTitle className="mb-0">Settings</CardTitle>
              <CardDescription>
                Password policy, account lockout, and login banner. Choose Edit to change values inline.
              </CardDescription>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end mt-2">
              {!isEditMode ? (
                <Button type="button" onClick={handleEnterEditMode} className="bg-primary text-primary-foreground hover:bg-primary/90">
                  Edit
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Button type="button" variant="outline" disabled={isLoading} onClick={handleCancelEditMode}>
                    Cancel
                  </Button>
                  <Button type="submit" form="access-control-settings-form" disabled={isLoading || errorsPresent} className="bg-primary text-primary-foreground hover:bg-primary/90">
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save
                  </Button>
                </div>
              )}
            </div>
          </div>
         <div className="-mx-6 border-t border-border/50" aria-hidden />
        </CardHeader>
        <CardContent className="pt-6">
           <form id="access-control-settings-form" autoComplete="off" className="flex flex-col gap-6" onSubmit={handleSaveClick}>
            <div className="space-y-6">
              <div className={`grid min-w-0 grid-cols-1 gap-x-16 md:grid-cols-2 ${isEditMode ? 'gap-y-6' : 'gap-y-3'}`}>
                <div className="flex min-w-0 flex-col gap-2">
                  {isEditMode ? (
                    <InputField
                      label="Password Age (Days)"
                      name={minimumAgeDays}
                      type="number"
                      controlSize="xs"
                      required
                      value={formData.minimumAgeDays}
                      placeholder="Enter password age in days"
                      onChange={onFieldChange}
                      onBlur={onMinAgeOrWarningDaysBlurHandler}
                      tooltipText={`Value should be between ${Math.max(1, formData.expiryWarningDays)} and 120`}
                    />
                  ) : (
                    renderReadOnlyField("Password Age (Days)", formData.minimumAgeDays, true)
                  )}
                </div>

                <div className="flex min-w-0 flex-col gap-2">
                  {isEditMode ? (
                    <InputField
                      label="Password Expiry Warning (Days)"
                      name={expiryWarningDays}
                      type="number"
                      controlSize="xs"
                      required
                      value={formData.expiryWarningDays}
                      placeholder="Enter expiry warning days"
                      onChange={onFieldChange}
                      onBlur={onMinAgeOrWarningDaysBlurHandler}
                      tooltipText={`Value should be between 1 and ${Math.min(120, formData.minimumAgeDays)}`}
                    />
                  ) : (
                    renderReadOnlyField("Password Expiry Warning (Days)", formData.expiryWarningDays, true)
                  )}
                </div>

                <div className="flex min-w-0 flex-col gap-2">
                  {isEditMode ? (
                    <InputField
                      label="Password Minimum Length"
                      name={minimumLength}
                      type="number"
                      controlSize="xs"
                      required
                      value={formData.minimumLength}
                      min={8}
                      max={100}
                      placeholder="Enter minimum length"
                      onChange={onFieldChange}
                      tooltipText="Value should be between 8 and 100"
                    />
                  ) : (
                    renderReadOnlyField("Password Minimum Length", formData.minimumLength, true)
                  )}
                </div>

                <div className="flex min-w-0 flex-col gap-2">
                  {isEditMode ? (
                    <InputField
                      label="Password Reuse Prevention Count"
                      name={reusePreventionCount}
                      type="number"
                      controlSize="xs"
                      required
                      value={formData.reusePreventionCount}
                      min={1}
                      max={6}
                      placeholder="Enter reuse prevention count"
                      onChange={onFieldChange}
                      tooltipText="Value should be between 1 and 6"
                    />
                  ) : (
                    renderReadOnlyField("Password Reuse Prevention Count", formData.reusePreventionCount, true)
                  )}
                </div>

                <div className="flex min-w-0 flex-col gap-2">
                  {isEditMode ? (
                    <InputField
                      label="Password Reuse Prevention (Days)"
                      name={reusePreventionDays}
                      type="number"
                      controlSize="xs"
                      required
                      value={formData.reusePreventionDays}
                      min={7}
                      max={365}
                      placeholder="Enter reuse prevention days"
                      onChange={onFieldChange}
                      tooltipText="Value should be between 7 and 365"
                    />
                  ) : (
                    renderReadOnlyField("Password Reuse Prevention (Days)", formData.reusePreventionDays, true)
                  )}
                </div>
              </div>
            </div>

            <div className="pt-4">
              <div 
                className={`grid min-w-0 grid-cols-1 gap-x-16 md:grid-cols-2 ${isEditMode ? 'gap-y-6 border-t pt-6' : 'gap-y-3'}`}
                style={isEditMode ? { borderColor: "color-mix(in srgb, var(--border) 50%, transparent)" } : undefined}
              >
                <div className="flex min-w-0 flex-col gap-2">
                  {isEditMode ? (
                    <div className="flex items-center gap-2 pb-2">
                      <Checkbox
                        id="accountLockoutEnabled"
                        checked={formData.accountLockoutEnabled}
                        onCheckedChange={(checked: boolean) =>
                          setFormData((prev: any) => ({ ...prev, accountLockoutEnabled: checked }))
                        }
                        className="m-0 p-0"
                      />
                      <Label htmlFor="accountLockoutEnabled" className="cursor-pointer">Account Lockout</Label>
                    </div>
                  ) : (
                    renderReadOnlyField("Account Lockout", formData.accountLockoutEnabled)
                  )}
                </div>
              </div>

              {formData.accountLockoutEnabled && (
                <div className={`min-w-0 rounded-md border border-border p-3 ${isEditMode ? ' mt-3' : 'mt-6 '}`} style={nestedPanelStyle}>
                  <div className={`grid min-w-0 grid-cols-1 gap-x-16 md:grid-cols-2 ${isEditMode ? 'gap-y-6' : 'gap-y-3 '}`}>

                    <div className="flex min-w-0 flex-col gap-2">
                      {isEditMode ? (
                        <InputField
                          label="Account Lockout Threshold"
                          name={accountLockoutFailedCount}
                          type="number"
                          controlSize="xs"
                          required={formData.accountLockoutEnabled}
                          disabled={!formData.accountLockoutEnabled}
                          value={formData.accountLockoutFailedCount}
                          min={1}
                          max={10}
                          placeholder="Enter lockout threshold"
                          onChange={onFieldChange}
                          handleDisabled={handleDisabled}
                          defaultValue={3}
                          tooltipText="Value should be between 1 and 10"
                        />
                      ) : (
                        renderReadOnlyField("Account Lockout Threshold", formData.accountLockoutFailedCount, formData.accountLockoutEnabled)
                      )}
                    </div>

                    <div className="flex min-w-0 flex-col gap-2">
                      {isEditMode ? (
                        <InputField
                          label="Inactivity Logout Duration (Minutes)"
                          name={inactivityLogoutDurationMinutes}
                          type="number"
                          controlSize="xs"
                          required
                          value={formData.inactivityLogoutDurationMinutes}
                          min={5}
                          max={30}
                          placeholder="Enter logout duration in minutes"
                          onChange={onFieldChange}
                          tooltipText="Value should be between 5 and 30"
                        />
                      ) : (
                        renderReadOnlyField("Inactivity Logout Duration (Minutes)", formData.inactivityLogoutDurationMinutes, true)
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="pt-2">
              <div 
                className={`grid min-w-0 grid-cols-1 gap-x-16 gap-y-3 md:grid-cols-2 ${isEditMode ? "border-t pt-6" : ""}`}
                style={isEditMode ? { borderColor: "color-mix(in srgb, var(--border) 50%, transparent)" } : undefined}
              >
                <div className="flex min-w-0 flex-col gap-2">
                  {isEditMode ? (
                    <div className="flex items-center gap-2 pb-2">
                      <Checkbox
                        id="bannerEnabled"
                        checked={formData.bannerEnabled}
                        onCheckedChange={(checked: boolean) =>
                          setFormData((prev: any) => ({ ...prev, bannerEnabled: checked }))
                        }
                        className="m-0 p-0"
                      />
                      <Label htmlFor="bannerEnabled" className="cursor-pointer">Login Banner Enabled</Label>
                    </div>
                  ) : (
                    renderReadOnlyField("Login Banner Enabled", formData.bannerEnabled)
                  )}
                </div>
              </div>

              {formData.bannerEnabled && (
                <div className={`min-w-0 rounded-md border border-border p-3 ${isEditMode ? ' mt-3' : 'mt-6 '}`} style={nestedPanelStyle}>
                  <div className="grid min-w-0 grid-cols-1 gap-x-16 gap-y-3 md:grid-cols-2">
                    <div className="flex min-w-0 flex-col gap-2 md:col-span-2">
                      {isEditMode ? (
                        <InputField
                          label="Banner Title"
                          name={bannerTitle}
                          controlSize="md"
                          required={formData.bannerEnabled}
                          disabled={!formData.bannerEnabled}
                          value={formData.bannerTitle}
                          maxLength={25}
                          placeholder="Enter banner title"
                          pattern={BANNER_PATTERN}
                          patternMsg={BANNER_PATTERN_MSG}
                          onChange={onFieldChange}
                          handleDisabled={handleDisabled}
                          defaultValue="Banner Title"
                        />
                      ) : (
                        renderReadOnlyField("Banner Title", formData.bannerTitle, formData.bannerEnabled)
                      )}
                    </div>

                    <div className="flex min-w-0 flex-col gap-2 md:col-span-2 mt-4">
                      {isEditMode ? (
                        <TextAreaField
                          label="Message of Day"
                          name={messageOfDay}
                          controlSize="full"
                          disabled={!formData.bannerEnabled}
                          value={formData.messageOfDay}
                          maxLength={100}
                          placeholder="Enter message of day"
                          pattern={BANNER_PATTERN}
                          patternMsg={BANNER_PATTERN_MSG}
                          onChange={onFieldChange}
                          handleDisabled={handleDisabled}
                          defaultValue=""
                        />
                      ) : (
                        <div className="flex flex-col gap-2">
                          <span className="text-sm text-muted-foreground">Message of Day</span>
                          <div className="whitespace-pre-wrap text-sm font-semibold text-foreground">{renderValue(formData.messageOfDay)}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
    
    </div>
  );
};